export const LOCALES = ['en', 'zh-CN', 'zh-TW'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export const LOCALE_COOKIE_NAME = 'NEXT_LOCALE';

/** One year, in seconds. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

interface LocaleMeta {
  /** Name of the language, written in that language. */
  label: string;
  /** Value for the `lang` attribute on `<html>`. */
  htmlLang: string;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { htmlLang: 'en', label: 'English' },
  'zh-CN': { htmlLang: 'zh-Hans', label: '简体中文' },
  'zh-TW': { htmlLang: 'zh-Hant', label: '繁體中文' },
};

export function isLocale(value: string | undefined): value is Locale {
  return !!value && LOCALES.includes(value as Locale);
}

function matchLocale(tag: string): Locale | null {
  if (tag === 'en' || tag.startsWith('en-')) {
    return 'en';
  }

  if (tag.startsWith('zh')) {
    const isTraditional = ['hant', 'tw', 'hk', 'mo'].some((marker) => tag.includes(marker));

    return isTraditional ? 'zh-TW' : 'zh-CN';
  }

  return null;
}

/**
 * Best-effort match of an `Accept-Language` header against the supported locales.
 * Any `zh` variant that isn't explicitly Traditional resolves to Simplified.
 *
 * Quality weights are honoured: entries are ranked by `q` (defaulting to 1), and
 * `q=0` means explicitly unacceptable, so `en;q=0, zh-CN` resolves to Chinese.
 */
export function resolveLocaleFromAcceptLanguage(header: string | null | undefined): Locale {
  if (!header) {
    return DEFAULT_LOCALE;
  }

  const ranked = header
    .split(',')
    .map((part, index) => {
      const [rawTag, ...params] = part.trim().split(';');
      const tag = rawTag?.trim().toLowerCase();

      const qParam = params.find((param) => param.trim().toLowerCase().startsWith('q='));
      const parsedQ = qParam ? Number.parseFloat(qParam.trim().slice(2)) : 1;
      const quality = Number.isFinite(parsedQ) ? parsedQ : 1;

      // `index` keeps the header's own ordering stable within equal weights.
      return { index, quality, tag };
    })
    .filter((entry): entry is { index: number; quality: number; tag: string } => !!entry.tag && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index);

  for (const { tag } of ranked) {
    const matched = matchLocale(tag);

    if (matched) {
      return matched;
    }
  }

  return DEFAULT_LOCALE;
}
